import React, {useCallback, useEffect, useRef, useState} from 'react'
import {Box} from '@mui/material'
import {
    flushInventoryItemsQueue,
    processFailedSaveResponse,
    processRequestItem,
    processSaveReportResponse,
    SaveInventoryItem,
    SaveInventoryRequestItem,
    saveReceipt
} from '../utils'
import {CachedProduct, getCachedProducts} from '../productCache'
import {webEvents} from '../../common/WebEvents'
import {useLocation, useNavigate} from 'react-router-dom'
import excludedSound from '../../assets/sounds/excluded.mp3'
import ReceiveSummary from './ReceiveSummary'
import ReceiveSerials from './ReceiveSerials'
import ReceiveNonSerials from './ReceiveNonSerials'
import {useDispatch} from 'react-redux'
import {createError} from '../../store/slices/error'

import {
    ExpectedNonSerials,
    ExpectedSerials,
    OpenReceiptResult,
    submitReceipt,
    TransferOrderResult
} from '../transferUtils'
import StepComponent from '../../common/StepComponent'
import {NotificationSnackbar} from '../../common/ui'


const Receive = () => {
    const location = useLocation()
    const transferOrder = location.state?.transferOrder as OpenReceiptResult
    const [serialItems, setSerialItems] = useState<SaveInventoryItem[]>([])
    const [expectedSerialItems, setExpectedSerialItems] = useState<ExpectedSerials[]>([])
    const [expectedNonSerialItems, setExpectedNonSerialItems] = useState<ExpectedNonSerials[]>([])
    const [nonSerialItems, setNonSerialItems] = useState<SaveInventoryItem[]>([])
    const [isSaving, setIsSaving] = useState(false)
    const [pendingSync, setPendingSync] = useState(false)
    const [pendingRequestItems, setPendingRequestItems] = useState<SaveInventoryRequestItem[] | null>([])
    const [pendingResponse, setPendingResponse] = useState<{items: SaveInventoryItem[], isSync: boolean} | null>(null)
    const [processQueue, setProcessQueue] = useState<boolean>(false)
    const [currentStep, setCurrentStep] = useState<number>(1)
    const [failedSaveReceiptResponse, setFailedSaveReceiptResponse] = useState<boolean>(false)
    const [openDialog, setOpenDialog] = useState(false)
    const navigate = useNavigate()
    const dispatch = useDispatch()

    const [entryErrors, setEntryErrors] = useState<string | null>(null)
    const [loading, setLoading] = useState<{[sku: string]: boolean}>({})
    const [qtyChanged, setQtyChanged] = useState(false)
    const loadingNonSerialItems = Object.values(loading).some(isLoading => isLoading)

    const audioPlayer = useRef<HTMLAudioElement | null>(null)
    const [products, setProducts] = useState<CachedProduct[]>([])
    const [missingItems, setMissingItems] = useState<{
        item: string,
        expected: number,
        missing: number,
        serial: boolean
    }[]>([])

    useEffect(() => {
        let receivingOpen = window.sessionStorage.getItem('receiving')
        if (!receivingOpen || !transferOrder) {
            navigate('/inventory/receive')
            return
        }
        const fetchProducts = async () => {
            const cachedProducts = await getCachedProducts()
            const allProducts = Object.values(cachedProducts)
            setProducts(allProducts)
        }
        processReceiveItems(null)
        fetchProducts()
    }, [])

    function playAudio() {
        if (audioPlayer.current) {
            audioPlayer.current.play().catch(error => {
                console.error('Error playing audio:', error)
            })
        }
    }

    const processReceiveItems = useCallback((requestItems: SaveInventoryRequestItem[] | null) => {
        const opened = window.sessionStorage.getItem('receiving')
        const id = transferOrder.transferOrderId
        if (!id || !opened) return
        let isSync = requestItems === null
        setIsSaving(true)
        saveReceipt(Number(id), requestItems)
            .then(items => {
                setPendingResponse({items, isSync})
            })
            .catch(error => {
                setIsSaving(false)
                webEvents.error(error.message, {receiving: id}, error.stack)
                playAudio()
                const causeMessage = error.cause ? error.cause.messages[0] : error.messages[0]
                dispatch(createError({
                    type: error.type,
                    message: causeMessage
                }))
                setFailedSaveReceiptResponse(true)
            })
    }, [])

    useEffect(() => {
        if (pendingRequestItems === null || pendingRequestItems.length)
            processReceiveItems(pendingRequestItems)
    }, [pendingRequestItems])

    useEffect(() => {
        if (failedSaveReceiptResponse) {
            const {updatedSerialItems, updatedNonSerialItems} = processFailedSaveResponse(serialItems, nonSerialItems)
            setSerialItems(updatedSerialItems)
            setNonSerialItems(updatedNonSerialItems)
            setProcessQueue(true)
        }
        return () => setFailedSaveReceiptResponse(false)
    }, [failedSaveReceiptResponse])

    useEffect(() => {
        if (!processQueue)
            return

        let result = flushInventoryItemsQueue(serialItems, nonSerialItems)

        if (result.serials)
            setSerialItems(result.serials)
        if (result.nonSerials)
            setNonSerialItems(result.nonSerials)

        setPendingRequestItems(result.requestItems ?? [])
        setProcessQueue(false)
    }, [processQueue])

    useEffect(() => {
        if (pendingResponse === null)
            return

        let {serials, nonSerials} = processSaveReportResponse(
            pendingResponse.items, serialItems, nonSerialItems, pendingResponse.isSync)
        if (serials)
            setSerialItems(serials)
        if (nonSerials)
            setNonSerialItems(nonSerials)

        setIsSaving(false)
        setPendingResponse(null)

        if (pendingResponse.isSync) {
            setPendingSync(false)
            setPendingRequestItems(null)
        } else if (pendingSync) {
            setPendingSync(false)
            setPendingRequestItems(null)
        } else {
            setProcessQueue(true)
        }
    }, [pendingResponse])

    useEffect(() => {
        if (transferOrder) {
            setupExpectedItemsLists(transferOrder)
        }
    }, [transferOrder])

    useEffect(() => {
        if (currentStep === 3) {
            getMissingItems()
        }
    }, [currentStep])


    function saveRequestItem(reqItem: SaveInventoryRequestItem) {
        let result = processRequestItem(reqItem, serialItems, nonSerialItems, isSaving || pendingSync)

        if (result === null) {
            playAudio()

            if (reqItem.serial)
                setEntryErrors(`Serial Item ${reqItem.serial} already added`)
            else
                setEntryErrors(`SKU Item ${reqItem.sku} already added`)
        } else {
            if (result.serials)
                setSerialItems(result.serials)

            if (result.nonSerials)
                setNonSerialItems(result.nonSerials)

            if (result.progress && !result.progress.startsWith('q-'))
                setPendingRequestItems([reqItem])
        }
    }

    const handleStepChange = () => {
        if (currentStep === 3) {
            processSubmitReceipt()
        } else {
            setCurrentStep(currentStep + 1)
        }
    }

    const handleBack = () => {
        if (currentStep === 1) return
        setCurrentStep(currentStep - 1)
    }

    const setupExpectedItemsLists = (orders: OpenReceiptResult | TransferOrderResult) => {
        const allExpectedSerials: React.SetStateAction<ExpectedSerials[]> = []
        const allExpectedNonSerials: React.SetStateAction<ExpectedNonSerials[]> = []
        orders.items.forEach(item => {
            if (item.serials.length > 0) {
                item.serials.forEach(serial => {
                    allExpectedSerials.push({serial: serial, quantity: 1})
                })
            } else {
                allExpectedNonSerials.push({
                    sku: item.sku,
                    model_number: item.model_number,
                    location: item.location || '',
                    quantity: item.quantity,
                    enteredAt: null
                })
            }
        })
        setExpectedSerialItems(allExpectedSerials)
        setExpectedNonSerialItems(allExpectedNonSerials)
    }

    function processSubmitReceipt() {
        const id = transferOrder.transferOrderId
        if (id)
            submitReceipt(id)
                .then(() => {
                    webEvents.info('Report submitted successfully', {
                        transferOrderId: id
                    })
                    window.sessionStorage.removeItem('receiving')
                    window.sessionStorage.setItem('receiveSuccess', 'true')
                    navigate('/inventory/receive/')
                })
                .catch(error => {
                    webEvents.error(
                        `Error processing report: ${error.message}`,
                        {transferOrderId: id},
                        error
                    )
                    playAudio()
                    const causeMessage = error.cause
                        ? error.cause.messages[0]
                        : error.messages[0]
                    dispatch(createError({
                        type: error.type,
                        message: causeMessage
                    }))
                })
    }

    const confirmSubmit = () => {
        setOpenDialog(true)
    }

    const handleClose = () => {
        setOpenDialog(false)
    }

    const handleConfirm = () => {
        processSubmitReceipt()
        handleClose()
    }

    const getMissingItems = () => {
        let missingSerialTotal: {item: string, expected: number, missing: number, serial: boolean}[] = []
        let missingNonSerialTotal: {item: string, expected: number, missing: number, serial: boolean}[] = []


        expectedSerialItems.forEach(expectedItem => {
            const foundInReport = serialItems.some(item => item.serial === expectedItem.serial)

            if (!foundInReport) {
                // This serial was missing entirely
                missingSerialTotal.push({
                    item: expectedItem.serial,
                    expected: expectedItem.quantity,
                    missing: expectedItem.quantity,
                    serial: true
                })
            }
        })


        expectedNonSerialItems.forEach(expectedItem => {
            const foundInReport = nonSerialItems.find(item => item.sku === expectedItem.sku)

            if (!foundInReport) {
                // This non-serial was missing entirely
                missingNonSerialTotal.push({
                    item: expectedItem.sku,
                    expected: expectedItem.quantity,
                    missing: expectedItem.quantity,
                    serial: false
                })
            } else if (foundInReport.quantity !== expectedItem.quantity) { //this non-serial was partially missing quantity
                missingNonSerialTotal.push({
                    item: expectedItem.sku,
                    expected: expectedItem.quantity,
                    missing: Math.abs(foundInReport.quantity - expectedItem.quantity),
                    serial: false
                })
            }
        })

        setMissingItems(missingSerialTotal.concat(missingNonSerialTotal))
    }

    const isStepDisabled = (type: 'back' | 'next') => {
        return type === 'next' && (loadingNonSerialItems || qtyChanged)
    }

    const getNextButtonLabel = (currentStep: number) => {
        if (currentStep === 1) return 'Next: Non-serials'
        if (currentStep === 2) return 'Review Report'
        return ''
    }

    const stepComponent = (
        <>
            {currentStep === 1 && <ReceiveSerials
                products={products}
                serialItems={serialItems}
                expectedSerials={expectedSerialItems}
                saveRequestItem={saveRequestItem}
            />}
            {currentStep === 2 && <ReceiveNonSerials
                products={products}
                nonSerialItems={nonSerialItems}
                expectedNonSerialItems={expectedNonSerialItems}
                saveRequestItem={saveRequestItem}
                loading={loading}
                setLoading={setLoading}
                qtyChanged={qtyChanged}
                setQtyChanged={setQtyChanged}
            />}
            {currentStep === 3 && <ReceiveSummary
                serialItems={serialItems}
                nonSerialItems={nonSerialItems}
                expectedSerialItems={expectedSerialItems}
                expectedNonSerialItems={expectedNonSerialItems}
                products={products}
            />}
        </>
    )

    return (
        <>
            {entryErrors && (
                <NotificationSnackbar
                    open={true}
                    message={entryErrors}
                    onClose={() => setEntryErrors(null)}
                    severity="error"
                    autoHideDuration={3000}
                    anchorOrigin={{vertical: 'top', horizontal: 'center'}}
                />
            )}
            <audio ref={audioPlayer} src={excludedSound} />

            <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                <StepComponent
                    stepComponent={stepComponent}
                    currentStep={currentStep}
                    handleBack={handleBack}
                    handleStepChange={handleStepChange}
                    confirmSubmit={confirmSubmit}
                    handleClose={handleClose}
                    handleConfirm={handleConfirm}
                    dialogContent={
                        missingItems.length > 0 ? 'There are missing items' : 'Proceed with submission'
                    }
                    dialogTitle="Confirm Submission"
                    dialogActions={{submitLabel: 'Receive'}}
                    openDialog={openDialog}
                    isStepDisabled={isStepDisabled}
                    getNextButtonLabel={getNextButtonLabel}
                />
            </Box>
        </>
    )
}

export default Receive
